import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Key, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SchoolSelect } from "../SchoolSelect";

const ROLES = ["admin", "principal", "teacher", "parent", "staff", "accounts"];
const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
const SYMS = "!@#$%&*?";

export const genPassword = () => {
  const r = (n) => crypto.getRandomValues(new Uint32Array(1))[0] % n;
  const base = Array.from({ length: 11 }, () => CHARS[r(CHARS.length)]);
  base.push(String(r(10)), SYMS[r(SYMS.length)], CHARS[r(26)]);
  return base.sort(() => r(3) - 1).join("");
};

const splitList = (t) => t.split(",").map((x) => x.trim()).filter(Boolean);

const PasswordField = ({ value, onChange, required, testId }) => (
  <div>
    <Label>{required ? "Password *" : "New password (leave blank to keep)"}</Label>
    <div className="flex gap-2 mt-1">
      <Input required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      <Button type="button" variant="outline" onClick={() => onChange(genPassword())} data-testid={`${testId}-generate`} title="Generate strong password"><Key size={16} /></Button>
      <Button type="button" variant="outline" disabled={!value} onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} title="Copy"><Copy size={16} /></Button>
    </div>
    <p className="text-xs text-zinc-500 mt-1">At least 10 characters with a letter, a number and a symbol. Share it privately.</p>
  </div>
);

const StudentPicker = ({ students, value, onChange }) => (
  <div>
    <Label>Linked children</Label>
    <div className="mt-1 max-h-36 overflow-y-auto border border-zinc-200 rounded-md p-2 space-y-1">
      {students.map((s) => (
        <label key={s.id} className="flex items-center gap-2 text-sm">
          <Checkbox checked={value.includes(s.id)} data-testid={`link-student-${s.id}`}
            onCheckedChange={(c) => onChange(c ? [...value, s.id] : value.filter((x) => x !== s.id))} />
          {s.name} · {s.class_name}-{s.section}
        </label>
      ))}
      {students.length === 0 && <div className="text-xs text-zinc-500">No students yet.</div>}
    </div>
  </div>
);

const useStudents = (open) => {
  const [students, setStudents] = useState([]);
  useEffect(() => { if (open) { api.get("/students").then((r) => setStudents(r.data)).catch(() => {}); } }, [open]);
  return students;
};

const ClassesField = ({ value, onChange, testId }) => (
  <div>
    <Label>Assigned classes (comma separated)</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. 10, 9-A" className="mt-1" data-testid={testId} />
    <p className="text-xs text-zinc-500 mt-1">Use &quot;10&quot; for every section of class 10, or &quot;9-A&quot; for one section.</p>
  </div>
);

const emptyForm = { email: "", name: "", password: "", role: "teacher", phone: "", classes: "", linked: [], school: "" };

export const CreateUserDialog = ({ onDone, master }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const students = useStudents(open && f.role === "parent");

  const submit = async (e) => {
    e.preventDefault();
    if (master && !f.school) { toast.error("Choose a school"); return; }
    try {
      await api.post("/users", {
        email: f.email, name: f.name, password: f.password, role: f.role, phone: f.phone || null,
        school_id: master && f.school !== "both" ? f.school : null,
        assigned_classes: f.role === "teacher" ? splitList(f.classes) : [],
        linked_student_ids: f.role === "parent" ? f.linked : [],
      });
      toast.success("User created");
      setOpen(false); setF(emptyForm); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-user-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" /> Add User</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Full name *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="user-name-input" /></div>
          <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" data-testid="user-email-input" /></div>
          <PasswordField required value={f.password} onChange={(v) => setF({ ...f, password: v })} testId="user-password-input" />
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {master && (
            <SchoolSelect value={f.school} allowBoth={f.role === "admin"} testId="user-school-select"
              onChange={(v) => setF({ ...f, school: v, linked: [] })} />
          )}
          {f.role === "teacher" && <ClassesField value={f.classes} onChange={(v) => setF({ ...f, classes: v })} testId="user-classes-input" />}
          {f.role === "parent" && <StudentPicker students={master ? students.filter((s) => s.school_id === f.school) : students} value={f.linked} onChange={(v) => setF({ ...f, linked: v })} />}
          <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1" /></div>
          <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="user-submit-btn">Create</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const EditUserDialog = ({ user, open, onOpenChange, onDone }) => {
  const [f, setF] = useState(null);
  const students = useStudents(open && user?.role === "parent");

  useEffect(() => {
    if (user) setF({ name: user.name, phone: user.phone || "", classes: (user.assigned_classes || []).join(", "), linked: user.linked_student_ids || [], password: "" });
  }, [user]);

  if (!user || !f) return null;

  const submit = async (e) => {
    e.preventDefault();
    const body = { name: f.name, phone: f.phone || null };
    if (user.role === "teacher") body.assigned_classes = splitList(f.classes);
    if (user.role === "parent") body.linked_student_ids = f.linked;
    if (f.password) body.password = f.password;
    try {
      await api.patch(`/users/${user.id}`, body);
      toast.success("User updated");
      onOpenChange(false); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit {user.email}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Full name *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="edit-user-name-input" /></div>
          {user.role === "teacher" && <ClassesField value={f.classes} onChange={(v) => setF({ ...f, classes: v })} testId="edit-user-classes-input" />}
          {user.role === "parent" && <StudentPicker students={students.filter((s) => s.school_id === user.school_id)} value={f.linked} onChange={(v) => setF({ ...f, linked: v })} />}
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1" /></div>
          <PasswordField value={f.password} onChange={(v) => setF({ ...f, password: v })} testId="edit-user-password-input" />
          <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="edit-user-submit-btn">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
